import React, { createContext, useContext, useState, useEffect } from 'react';

const CurrencyContext = createContext();

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrency] = useState('USD');
  const [adaPrice, setAdaPrice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchAdaPrice = async () => {
    setLoading(true);
    setError(null);
    try {
      // Primary: Binance — highest ADA liquidity, no rate limits, no key required
      const res = await fetch('https://api.binance.com/api/v3/ticker/price?symbol=ADAUSDT');
      if (!res.ok) throw new Error('Binance fetch failed');
      const data = await res.json();
      const price = parseFloat(data.price);
      if (!price || Number.isNaN(price)) throw new Error('Binance returned invalid price');
      setAdaPrice(price);
    } catch (primaryErr) {
      console.warn('[CurrencyProvider] Binance failed, falling back to CoinGecko:', primaryErr.message);
      try {
        // Fallback: CoinGecko
        const res = await fetch('https://api.coingecko.com/api/v3/simple/price?ids=cardano&vs_currencies=usd');
        if (!res.ok) throw new Error('CoinGecko fetch failed');
        const data = await res.json();
        setAdaPrice(data.cardano.usd);
      } catch (fallbackErr) {
        setError(fallbackErr.message);
        console.error('[CurrencyProvider] Both price sources failed:', fallbackErr.message);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdaPrice();
    // Refresh price every 5 minutes
    const interval = setInterval(fetchAdaPrice, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const toggleCurrency = () => {
    setCurrency(currency === 'ADA' ? 'USD' : 'ADA');
  };

  const formatCurrency = (value, options = {}) => {
    const { decimals = 1, suffix = 'M', showSymbol = true } = options;

    if (currency === 'USD' && adaPrice) {
      const usdValue = value * adaPrice;
      const symbol = showSymbol ? '$' : '';
      return `${symbol}${usdValue.toFixed(decimals)}${suffix}`;
    } else {
      const symbol = showSymbol ? '₳' : '';
      return `${symbol}${value.toFixed(decimals)}${suffix}`;
    }
  };

  const getCurrencySymbol = () => {
    // Only show $ once the ADA price is loaded; until then values are still in ADA
    return currency === 'USD' && adaPrice ? '$' : '₳';
  };

  const convertValue = (adaValue) => {
    if (currency === 'USD' && adaPrice) {
      return adaValue * adaPrice;
    }
    return adaValue;
  };

  const value = {
    currency,
    adaPrice,
    loading,
    error,
    toggleCurrency,
    formatCurrency,
    getCurrencySymbol,
    convertValue,
    refreshPrice: fetchAdaPrice
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

export default CurrencyProvider;